import { QuestionBase } from './application-questions/question-base';
import { PhoneQuestion } from './application-questions/question-phone';
import { EmailQuestion } from './application-questions/question-email';
import { TextboxQuestion } from './application-questions/question-textbox';
import { DropdownQuestion } from './application-questions/question-dropdown';
import { CurrencyQuestion } from './application-questions/question-currency';
import { NumberQuestion } from './application-questions/question-number';
import { DatePickerQuestion } from './application-questions/question-date';
import { US_STATES } from '../services/location.service';
import { StateSelectorQuestion } from './application-questions/question-state-selector';
import { BooleanDropdownQuestion } from './application-questions/question-boolean-dropdown';

/** An clientApp field is just any of the available clientApp fields a client can have in an clientApp */
export class ApplicationField {
    id: string;
    additionalInfo: string;
    applicationSection:
        | 'tenant'
        | 'rental history'
        | 'employment'
        | 'additional income'
        | 'emergency contact'
        | 'vehicles'
        | 'additional info';
    childField: ApplicationField | null;
    defaultSort: number;
    dependsOn: string;
    doAllowMultiple: boolean;
    doAllowNone: boolean;
    fieldLabel: string;
    fieldType:
        | 'string'
        | 'boolean'
        | 'number'
        | 'date'
        | 'state-selector'
        | 'enum';
    fieldTypeIcon: string;
    isSelected: boolean;

    question: QuestionBase<any>;

    constructor(body: any) {
        this.id = body.id;
        this.additionalInfo = body.additionalInfo;
        this.applicationSection = body.applicationSection;
        this.childField = null;
        this.defaultSort = body.defaultSort;
        this.dependsOn = body.dependsOn;
        this.doAllowMultiple =
            body.allowMultiple || body.doAllowMultiple || false;
        this.doAllowNone = body.allowNone || body.doAllowNone || false;
        this.fieldLabel = body.fieldLabel;
        this.fieldType = body.fieldType;
        this.fieldTypeIcon = this.getFieldTypeIcon(body.fieldType);
        this.isSelected = true;

        this.question = this.getQuestionObject(body);
    }

    /** Choose an icon based on field type */
    getFieldTypeIcon(fieldType: string): string {
        switch (fieldType) {
            case 'string': {
                return 'bi bi-cursor-text';
            }
            case 'boolean': {
                return 'bi bi-ui-radios';
            }
            case 'number': {
                return 'bi bi-123';
            }
            case 'date': {
                return 'bi bi-calendar-event';
            }
            case 'state-selector':
            case 'enum':
                return 'bi bi-menu-button-wide-fill';
            default: {
                return 'bi bi-cursor-text';
            }
        }
    }

    /** Figure out which dynamic question to render */
    getQuestionObject(body: any): QuestionBase<any> {
        const options = {
            key: body.id,
            label: body.fieldLabel,
            order: body.defaultSort,
            required: true,
        };

        switch (body.fieldType) {
            case 'string': {
                if (body.fieldLabel.includes('Phone')) {
                    return new PhoneQuestion({
                        ...options,
                        type: 'phone',
                    });
                } else if (body.fieldLabel.includes('Email')) {
                    return new EmailQuestion({
                        ...options,
                        type: 'email',
                    });
                } else {
                    return new TextboxQuestion(options);
                }
            }
            case 'boolean': {
                return new BooleanDropdownQuestion({
                    ...options,
                    options: [
                        { value: '-- Yes / No --', key: '' },
                        { value: 'Yes', key: '1' },
                        { value: 'No', key: '0' },
                    ],
                });
            }
            case 'enum': {
                return new DropdownQuestion({
                    ...options,
                    options: body.additionalInfo
                        .split('|')
                        .map((val: string) => {
                            return {
                                value: val,
                                key: val,
                            };
                        }),
                });
            }
            case 'number': {
                if (body.fieldLabel.includes('Income')) {
                    return new CurrencyQuestion(options);
                } else {
                    return new NumberQuestion(options);
                }
            }
            case 'date': {
                return new DatePickerQuestion(options);
            }
            case 'state-selector': {
                const a = new StateSelectorQuestion({
                    ...options,
                    options: US_STATES.map((s) => {
                        return {
                            key: s.abbr,
                            value: s.name,
                        };
                    }),
                });

                return a;
            }
            default: {
                return new TextboxQuestion(options);
            }
        }
    }
}
